import { useState } from "react";
import { z } from "zod";
import { ArrowRight, CheckCircle2, MessageCircle, Loader2 } from "lucide-react";
import { PRODUCTS, BRAND } from "../data/site";
import { submitLead, buildEnquiryWhatsApp, openWhatsApp } from "../lib/api";

const Schema = z.object({
    name: z.string().min(2, "Please enter your full name"),
    company: z.string().min(2, "Company name is required"),
    country: z.string().min(2, "Country is required"),
    product: z.string().min(1, "Choose a product"),
    quantity: z.string().min(1, "Estimated quantity is required"),
    email: z.string().email("Enter a valid email"),
    whatsapp: z.string().min(6, "WhatsApp number is required"),
    message: z.string().max(800, "Keep message under 800 chars").optional().or(z.literal("")),
});

const initial = {
    name: "",
    company: "",
    country: "",
    product: "",
    quantity: "",
    email: "",
    whatsapp: "",
    message: "",
};

export default function LeadForm({ defaultProduct = "", source = "inquiry", compact = false }) {
    const [data, setData] = useState({ ...initial, product: defaultProduct });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [waUrl, setWaUrl] = useState("");

    const onChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const r = Schema.safeParse(data);
        if (!r.success) {
            const errs = {};
            r.error.issues.forEach((i) => (errs[i.path[0]] = i.message));
            setErrors(errs);
            return;
        }
        // Pre-open a tab during this user gesture so popup-blockers allow it.
        const pre = window.open("about:blank", "_blank");
        setStatus("submitting");
        const payload = { ...data, source };
        const wa = buildEnquiryWhatsApp(payload, BRAND.leadWhatsapp);
        // Fire-and-forget save to backend; UX should not depend on it.
        submitLead(payload).catch((err) => {
            console.warn("Lead save failed (continuing to WhatsApp):", err?.message || err);
        });
        // Hand off to WhatsApp immediately.
        openWhatsApp(pre, wa);
        setWaUrl(wa);
        setStatus("success");
        setData({ ...initial, product: defaultProduct });
    };

    if (status === "success") {
        return (
            <div
                data-testid="lead-success"
                className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8"
            >
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 text-emerald-600" />
                    <div>
                        <h3 className="font-display text-xl font-bold text-emerald-900">
                            ✓ Thank you. Your enquiry has been received.
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-emerald-900/80">
                            Please send the WhatsApp message we just opened — it carries all your
                            enquiry details and connects you directly with our export team. If the
                            WhatsApp tab didn&apos;t open, tap the button below.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="lead-continue-whatsapp"
                                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:scale-[1.03] transition-transform"
                            >
                                <MessageCircle className="h-4 w-4" />
                                Open WhatsApp message
                            </a>
                            <button
                                onClick={() => setStatus("idle")}
                                data-testid="lead-new-inquiry"
                                className="inline-flex items-center gap-2 rounded-full border border-emerald-300 px-5 py-3 text-sm font-semibold text-emerald-900 hover:bg-white"
                            >
                                Submit another inquiry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="lead-form"
            className={`rounded-3xl border border-hairline bg-neutral-bg ${
                compact ? "p-5 sm:p-7" : "p-6 sm:p-10"
            }`}
            noValidate
        >
            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name *" name="name" value={data.name} onChange={onChange} error={errors.name} testid="lf-name" />
                <Field label="Company *" name="company" value={data.company} onChange={onChange} error={errors.company} testid="lf-company" />
                <Field label="Country *" name="country" value={data.country} onChange={onChange} error={errors.country} testid="lf-country" />
                <SelectField
                    label="Product interested *"
                    name="product"
                    value={data.product}
                    onChange={onChange}
                    error={errors.product}
                    options={PRODUCTS.map((p) => ({ value: p.id, label: p.name }))}
                    testid="lf-product"
                />
                <Field label="Quantity required *" name="quantity" placeholder="e.g. 20 MT / 1x40 FCL" value={data.quantity} onChange={onChange} error={errors.quantity} testid="lf-quantity" />
                <Field label="Email *" name="email" type="email" value={data.email} onChange={onChange} error={errors.email} testid="lf-email" />
                <Field label="WhatsApp number *" name="whatsapp" placeholder="+971 50 000 0000" value={data.whatsapp} onChange={onChange} error={errors.whatsapp} testid="lf-whatsapp" />
            </div>
            <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink-muted">
                    Message
                </label>
                <textarea
                    name="message"
                    value={data.message}
                    onChange={onChange}
                    data-testid="lf-message"
                    rows={3}
                    placeholder="Specs, target price, delivery port…"
                    className="w-full resize-none rounded-2xl border border-hairline bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:border-brand-red focus:outline-none"
                />
                {errors.message && <p className="mt-1 text-xs text-brand-red">{errors.message}</p>}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-ink-muted">
                    We respond to export enquiries within 24 hours.
                </p>
                <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="lf-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-red px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
                >
                    {status === "submitting" ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                        </>
                    ) : (
                        <>
                            Send inquiry <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </button>
            </div>

            {status === "error" && (
                <div data-testid="lead-error" className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                    We are experiencing a temporary issue. Please contact us via WhatsApp at{" "}
                    <a className="font-semibold underline" href="https://wa.me/919491085798" target="_blank" rel="noreferrer">
                        +91 94910 85798
                    </a>{" "}
                    or email{" "}
                    <a className="font-semibold underline" href={`mailto:${BRAND.email}`}>
                        {BRAND.email}
                    </a>.
                </div>
            )}
        </form>
    );
}

function Field({ label, name, value, onChange, error, type = "text", placeholder, testid }) {
    return (
        <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink-muted">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:border-brand-red focus:outline-none"
            />
            {error && <p className="mt-1 text-xs text-brand-red">{error}</p>}
        </div>
    );
}

function SelectField({ label, name, value, onChange, error, options, testid }) {
    return (
        <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink-muted">
                {label}
            </label>
            <select
                name={name}
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full rounded-2xl border border-hairline bg-white px-4 py-3 text-sm text-ink focus:border-brand-red focus:outline-none"
            >
                <option value="">Select a product…</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            {error && <p className="mt-1 text-xs text-brand-red">{error}</p>}
        </div>
    );
}
